import IndicatorBadge from "@/components/status/IndicatorBadge";
import { encryptData, encryptSecret } from "@/features/cryptography";
import { RemoteJobTable } from "@/features/remote_jobs";
import { store, StoreState } from "@/store";
import { supabaseApi, useGetSeveritiesQuery } from "@/store/services/supabase";
import { PortfolioExportPendingKeys } from "@/tools/aggregate/portfolio-export/components/PortfolioExportPendingKeys";
import { PortfolioPasswordInput } from "@/tools/aggregate/portfolio-export/components/PortfolioPasswordInput";
import { ANALYSIS_PAGE_SIZE } from "@/tools/aggregate/portfolio-export/constants";
import { Tool } from "@/types/tools";
import { arrayBufferToString } from "@/utils/crypto";
import { useKeys } from "@/utils/hooks/useKeys";
import WarningOutlined from "@ant-design/icons/es/icons/WarningOutlined";
import { Alert, Modal, notification } from "antd";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { SeveritiesFairUseWarning } from "./components/SeveritiesFairUseWarning";
import { SeveritiesJobTableBody } from "./components/SeveritiesJobTableBody";
import { SeveritiesNewRunModal } from "./components/SeveritiesNewRunModal";
import { useOrgKeyExchange } from "./hooks/useOrgKeyExchange";
import { useSeveritiesJobs } from "./hooks/useSeveritiesJobs";
import type { SeveritiesJobSchema } from "./types";
import { toSeveritiesJobSchemas } from "./utils/supabase";
type GetSeveritiesResult = (typeof supabaseApi.endpoints.getSeverities)["Types"]["ResultType"];
const SeveritiesTool = () => {
  const [keyPair] = useKeys();
  const [notify, notificationContext] = notification.useNotification();
  const {
    t
  } = useTranslation("terms");
  const {
    currentUser,
    alerts,
    hasJustReceivedKeys,
    isAwaitingOrgKeys,
    isReady: isKeysStateReady,
    isPendingKeysOpen,
    closePendingKeys
  } = useOrgKeyExchange();
  const [currentPage, setCurrentPage] = useState(1);
  const severities = useGetSeveritiesQuery({
    page: currentPage,
    pageSize: ANALYSIS_PAGE_SIZE,
    isPrivate: isAwaitingOrgKeys
  }, {
    skip: !keyPair.user || !isKeysStateReady
  });
  // Remembers which response the rows came from, so a new page reads as loading until it's decrypted.
  const [decrypted, setDecrypted] = useState<{
    from: GetSeveritiesResult;
    rows: SeveritiesJobSchema[];
  }>();
  const {
    progress,
    unprocessed
  } = useSelector((state: StoreState) => state.severities);
  // Posts and tracks the current user's runs while this page is open; progress lands in the slice above.
  useSeveritiesJobs();
  useEffect(() => {
    const {
      data
    } = severities;
    if (!data || !keyPair.user) return;
    let cancelled = false;
    toSeveritiesJobSchemas(data.items, keyPair.user, keyPair.org).then(rows => {
      if (!cancelled) setDecrypted({
        from: data,
        rows
      });
    });
    return () => {
      cancelled = true;
    };
  }, [severities.data, keyPair.user, keyPair.org]);
  const orgKeyAlerts = <>
      <Modal closable onCancel={closePendingKeys} width={540} open={isPendingKeysOpen} footer={null}>
        <PortfolioExportPendingKeys />
      </Modal>
      {alerts.map(({
      key,
      ...alert
    }) => <Alert key={key} className="mb-6 p-4" {...alert} />)}
    </>;
  if (!keyPair.user || currentUser?.has_org_keys && !keyPair.org) {
    return <>
        {orgKeyAlerts}
        <PortfolioPasswordInput prompt={keyPair.user && hasJustReceivedKeys ? "You have been granted your organisation’s encryption keys. Verify your password to confirm the exchange." : undefined} isLoading={keyPair.isLoading} />
      </>;
  }

  // Live counts apply until the list refreshes with the run's next status.
  const rows = (decrypted?.rows ?? []).map(row => {
    if (row.status === "processing" && progress[row.id]) return {
      ...row,
      assets: progress[row.id]
    };
    if (row.status === "uploading" && unprocessed[row.id] !== undefined) {
      return {
        ...row,
        assets: {
          ...row.assets,
          unprocessedCount: unprocessed[row.id]
        }
      };
    }
    return row;
  });
  return <>
      {notificationContext}
      {orgKeyAlerts}
      {severities.isError && <Alert className="mb-6 p-4" type="error" showIcon message="Couldn't load severities runs" description="Please refresh your browser and try again." />}
      <Alert rootClassName="border-none mb-4 p-4" type="warning" description={<div className="text-yellow-800">
            <p className="relative mb-2 ml-10 text-base font-semibold">
              <IndicatorBadge mode="inline" placement="left" offset={[-6, -3]}>
                {t("severities")} (BETA)
              </IndicatorBadge>
            </p>
            <p className="mb-2">
              Hazard Severities is a new tool within the XDI Hub that provides insight into the change in the physical
              magnitude of fixed probability climate events over time. Initial Beta release supports CSV-based analysis
              and downloadable results only. Interactive results visualisations are planned for February 2027.
            </p>
            <p>
              Please note: Some underlying hazard methodologies are still being refined, and results may change as
              models are updated.
            </p>
          </div>} data-sentry-element="Alert" data-sentry-source-file="index.tsx" />
      <RemoteJobTable<SeveritiesJobSchema> jobSchemas={rows}
    // The modal closes itself; createSeveritiesRun refreshes the list.
    onRunStart={() => {}} headerProps={{
      newRunModalComponent: SeveritiesNewRunModal as FC
    }} data-sentry-element="RemoteJobTable" data-sentry-source-file="index.tsx">
        <SeveritiesFairUseWarning data-sentry-element="SeveritiesFairUseWarning" data-sentry-source-file="index.tsx" />
        <SeveritiesJobTableBody rows={rows} isLoading={!severities.isError && (!severities.data || decrypted?.from !== severities.data)} total={severities.data?.count ?? 0} currentPage={currentPage} pageSize={ANALYSIS_PAGE_SIZE} onPageChange={setCurrentPage} onDelete={async (id: string) => {
        const res = await store.dispatch(supabaseApi.endpoints.deleteSeverities.initiate(id));
        if ("error" in res && res.error) {
          console.error(res.error);
          notify.error({
            message: "Error deleting severities run",
            description: "message" in res.error ? res.error.message : "Something went wrong. Please try again."
          });
        }
      }} onEdit={async ({
        schema,
        name,
        isPrivate,
        groupId
      }) => {
        if (!keyPair.user) return;

        // Re-encrypts the run's secret for whoever can now open it.
        let encryptedSecret = "";
        if (isPrivate) {
          encryptedSecret = arrayBufferToString(await encryptSecret({
            publicKey: keyPair.user.keyPair.publicKey,
            secret: schema.secret.secretAndSalt
          }));
        } else if (keyPair.orgSecret && keyPair.org?.keyPair.publicKey) {
          encryptedSecret = arrayBufferToString(await encryptSecret({
            publicKey: keyPair.org.keyPair.publicKey,
            secret: schema.secret.secretAndSalt
          }));
        }
        const res = await store.dispatch(supabaseApi.endpoints.updateSeveritiesDetails.initiate({
          id: schema.id,
          name: await encryptData(schema.secret, name),
          groupId,
          isPrivate,
          secretEnc: encryptedSecret
        }));
        if ("error" in res && res.error) {
          console.error(res.error);
          notify.error({
            message: "Error updating severities run",
            description: "message" in res.error ? res.error.message : "Something went wrong. Please try again."
          });
        } else {
          notify.success({
            message: "Hazard Severities run updated!"
          });
        }
      }} data-sentry-element="SeveritiesJobTableBody" data-sentry-source-file="index.tsx" />
      </RemoteJobTable>
    </>;
};
export default {
  id: "severities",
  category: "comprehensive",
  keyPrefix: "comprehensive.severities",
  icon: <WarningOutlined />,
  render: () => <SeveritiesTool />,
  showUsage: true
} as Tool;